import React, { useEffect, useRef, useState } from "react";
import { withRouter } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { message } from "antd";
import AuthenticationApiSerivce from "../../../../api/AuthServices";
import {
  isRegistrationLoadRetryExhaustedError,
  isRequestCancelledError,
} from "../../../../api/requestRetry";
import { RegistrationLayout } from "../../../RegistrationLayout/RegistrationLayout";
import { RegistrationSetupNavbar } from "../../../RegistrationLayout/RegistrationSetupNavbar";
import RegistrationSetupWrapper from "../../RegistrationSetup.style";
import {
  REGISTRATION_SETUP_STEP_META,
  REGISTRATION_SETUP_TOTAL_STEPS,
} from "../../../../helper/constants";
import {
  buildNoPreferenceAppointmentSlot,
  buildNoPreferenceSelectedAppointment,
  fetchRegistrationNoPreferenceAppointmentWeek,
  getNoPreferenceSlotData,
  mapRdInfoListToSelectionCards,
  persistNoPreferenceRdSelection,
  persistNoPreferenceSlotSelection,
} from "../registrationNoPreferenceTimeslots";
import {
  getRegistrationData,
  updateRegistrationData,
  isCardOnFileComplete,
} from "../../registrationSetupStorage";
import { registrationStepBack } from "../../registrationNavigation";
import { trackRegistrationProviderSelected } from "../../../../helper/registrationAnalytics";
import RegistrationLoadRetryModal from "../../RegistrationLoadRetryModal";
import RegistrationNoPreferenceRdSlotModal from "../RegistrationNoPreferenceRdSlotModal/RegistrationNoPreferenceRdSlotModal";
import {
  getTimeslotWeekOffsetFromStartDate,
  mapRdSlotResponseToAppointmentWeek,
  MAX_TIMESLOT_WEEK_OFFSET,
} from "../registrationAppointmentTimeslots";

const APPOINTMENT_STEP = REGISTRATION_SETUP_STEP_META.APPOINTMENT_TIME;

const getAppointmentStepEyebrowLabel = (translate) => {
  const stepLabel = translate("NUTRITION_COMMON_LBL_STEP_OF", {
    current: APPOINTMENT_STEP.step,
    total: REGISTRATION_SETUP_TOTAL_STEPS,
  });
  const eyebrow = translate("NUTRITION_TIMESLOT_LBL_EYEBROW");
  const separatorIndex = eyebrow.indexOf("—");
  const suffix =
    separatorIndex === -1 ? eyebrow : eyebrow.slice(separatorIndex + 1).trim();
  return `${stepLabel} — ${suffix}`.toUpperCase();
};

const SLOT_SECTION_KEYS = {
  morning: "SLOT_SECTION_MORNING",
  afternoon: "SLOT_SECTION_AFTERNOON",
  evening: "SLOT_SECTION_EVENING",
};

const RegistrationNoPreferenceAppointmentTime = ({ history }) => {
  const { t, i18n } = useTranslation();
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [appointmentWeek, setAppointmentWeek] = useState(null);
  const [weekOffset, setWeekOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isWeekNavigating, setIsWeekNavigating] = useState(false);
  const [isRdModalOpen, setIsRdModalOpen] = useState(false);
  const [modalSlotLabel, setModalSlotLabel] = useState("");
  const [modalDietitians, setModalDietitians] = useState([]);
  const [loadRetryModal, setLoadRetryModal] = useState({
    isOpen: false,
    onRetry: null,
  });
  const loadAppointmentWeekRef = useRef(null);
  const weekNavControllerRef = useRef(null);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById("root");
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevRootHeight = root?.style.height ?? "";

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    if (root) {
      root.style.height = "100vh";
      root.style.height = "100dvh";
    }

    const data = getRegistrationData();
    if (!isCardOnFileComplete(data)) {
      history.replace("/register/account/card");
      return () => {
        html.style.overflow = prevHtmlOverflow;
        body.style.overflow = prevBodyOverflow;
        if (root) root.style.height = prevRootHeight;
      };
    }

    const savedSlot = data.noPreferenceSelectedSlot ?? null;
    if (savedSlot?.day && savedSlot?.time) {
      setSelectedSlot(savedSlot);
    }

    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      if (root) {
        root.style.height = prevRootHeight;
      }
    };
  }, [history]);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    const loadAppointmentWeek = async (offset = 0, options = {}) => {
      const { force = false } = options;
      const data = getRegistrationData();
      const cachedPayload = data.noPreferenceTimeslotRequest;
      const cachedRdSlot = data.noPreferenceRdSlot;

      if (!force && cachedPayload?.start_date && cachedRdSlot) {
        const cachedWeek = mapRdSlotResponseToAppointmentWeek(
          cachedRdSlot,
          cachedPayload.start_date,
          cachedPayload.end_date,
          cachedPayload.timezone
        );
        const cachedOffset = getTimeslotWeekOffsetFromStartDate(
          cachedPayload.start_date,
          cachedPayload.timezone
        );

        if (!cancelled) {
          if (
            cachedWeek.days.length !==
            (data.noPreferenceAppointmentWeek?.days?.length ?? 0)
          ) {
            updateRegistrationData({ noPreferenceAppointmentWeek: cachedWeek });
          }
          setWeekOffset(cachedOffset);
          setAppointmentWeek(cachedWeek);
          setIsLoading(false);
          setIsWeekNavigating(false);
        }
        return;
      }

      if (force) {
        setIsWeekNavigating(true);
      } else {
        setIsLoading(true);
      }
      setLoadRetryModal({ isOpen: false, onRetry: null });
      try {
        const timeslotResult =
          await fetchRegistrationNoPreferenceAppointmentWeek({
            language: i18n.language,
            apiService: AuthenticationApiSerivce,
            signal: controller.signal,
            weekOffset: offset,
            history,
          });

        if (cancelled) {
          return;
        }

        if (timeslotResult?.redirected) {
          return;
        }

        updateRegistrationData({
          selectedDietitian: {
            id: "no-preference",
            name: t("NUTRITION_RDLIST_VAL_NO_PREFERENCE"),
            noPreference: true,
          },
          noPreferenceTimeslotRequest: timeslotResult.payload,
          noPreferenceRdSlot: timeslotResult.rdSlot,
          noPreferenceSlotsByKey: timeslotResult.slotsByKey,
          noPreferenceAppointmentWeek: timeslotResult.appointmentWeek,
          noPreferenceSelectedSlot: undefined,
          noPreferenceSelectedRd: undefined,
        });
        setWeekOffset(offset);
        setSelectedSlot(null);
        setAppointmentWeek(timeslotResult.appointmentWeek);
      } catch (err) {
        if (cancelled || isRequestCancelledError(err)) {
          return;
        }
        if (isRegistrationLoadRetryExhaustedError(err)) {
          setAppointmentWeek(null);
          setLoadRetryModal({
            isOpen: true,
            onRetry: () => loadAppointmentWeekRef.current?.(offset, { force }),
          });
          return;
        }
        const errorMessage = err?.response?.data?.message || err?.message || "";
        message.error(
          errorMessage.startsWith("NUTRITION_")
            ? t(errorMessage)
            : errorMessage || t("NUTRITION_TIMESLOT_ERR_LOAD")
        );
        if (!force) {
          history.replace("/register/account/dietitians");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsWeekNavigating(false);
        }
      }
    };

    loadAppointmentWeekRef.current = loadAppointmentWeek;
    loadAppointmentWeek(0);

    return () => {
      cancelled = true;
      controller.abort();
      weekNavControllerRef.current?.abort();
    };
  }, [history, i18n.language, t]);

  const handleWeekChange = async (direction) => {
    const nextOffset = weekOffset + direction;
    if (
      nextOffset < 0 ||
      nextOffset > MAX_TIMESLOT_WEEK_OFFSET ||
      isLoading ||
      isWeekNavigating
    ) {
      return;
    }

    weekNavControllerRef.current?.abort();
    const controller = new AbortController();
    weekNavControllerRef.current = controller;

    setIsWeekNavigating(true);
    setIsRdModalOpen(false);
    setLoadRetryModal({ isOpen: false, onRetry: null });
    try {
      const timeslotResult = await fetchRegistrationNoPreferenceAppointmentWeek({
        language: i18n.language,
        apiService: AuthenticationApiSerivce,
        signal: controller.signal,
        weekOffset: nextOffset,
        history,
      });

      if (timeslotResult?.redirected) {
        return;
      }

      updateRegistrationData({
        selectedDietitian: {
          id: "no-preference",
          name: t("NUTRITION_RDLIST_VAL_NO_PREFERENCE"),
          noPreference: true,
        },
        noPreferenceTimeslotRequest: timeslotResult.payload,
        noPreferenceRdSlot: timeslotResult.rdSlot,
        noPreferenceSlotsByKey: timeslotResult.slotsByKey,
        noPreferenceAppointmentWeek: timeslotResult.appointmentWeek,
        noPreferenceSelectedSlot: undefined,
        noPreferenceSelectedRd: undefined,
      });
      setWeekOffset(nextOffset);
      setSelectedSlot(null);
      setAppointmentWeek(timeslotResult.appointmentWeek);
    } catch (err) {
      if (isRequestCancelledError(err)) {
        return;
      }
      if (isRegistrationLoadRetryExhaustedError(err)) {
        setLoadRetryModal({
          isOpen: true,
          onRetry: () => handleWeekChange(direction),
        });
        return;
      }
      const errorMessage = err?.response?.data?.message || err?.message || "";
      message.error(
        errorMessage.startsWith("NUTRITION_")
          ? t(errorMessage)
          : errorMessage || t("NUTRITION_TIMESLOT_ERR_LOAD")
      );
    } finally {
      setIsWeekNavigating(false);
    }
  };

  const openRdModalForSlot = (slot) => {
    const data = getRegistrationData();
    const slotData =
      getNoPreferenceSlotData(data.noPreferenceSlotsByKey, slot) ?? slot.raw;
    const rdList = persistNoPreferenceSlotSelection({
      selectedSlot: slot,
      slotData,
      updateRegistrationData,
    });

    setSelectedSlot(slot);
    setModalSlotLabel(`${slot.day} · ${slot.time}`);
    setModalDietitians(mapRdInfoListToSelectionCards(rdList, slot.startTime));
    setIsRdModalOpen(true);
  };

  const handleSelectSlot = (cell, rowTime) => {
    if (cell.status !== "available") {
      return;
    }

    const slot = buildNoPreferenceAppointmentSlot(cell, rowTime);
    openRdModalForSlot(slot);
  };

  const handleViewProfile = (rd) => {
    if (!selectedSlot) {
      return;
    }

    persistNoPreferenceRdSelection({
      rdCard: rd,
      selectedSlot,
      updateRegistrationData,
    });
    setIsRdModalOpen(false);
    trackRegistrationProviderSelected(rd.id);
    history.push(`/register/account/no-preference/dietitians/${rd.id}`);
  };

  const handleBookAppointment = (rd) => {
    if (!selectedSlot) {
      return;
    }

    persistNoPreferenceRdSelection({
      rdCard: rd,
      selectedSlot,
      updateRegistrationData,
    });

    const selectedAppointment = buildNoPreferenceSelectedAppointment({
      selectedSlot,
      rdCard: rd,
    });

    updateRegistrationData({ selectedAppointment });
    setIsRdModalOpen(false);
    trackRegistrationProviderSelected(rd.id);

    if (selectedAppointment.schedulingUrl) {
      history.push("/calendly-booking");
      return;
    }

    history.push("/register/account/confirmation");
  };

  const hasAvailableSlots =
    appointmentWeek?.periods?.some((period) =>
      period.rows.some((row) =>
        row.cells.some((cell) => cell.status === "available")
      )
    ) ?? false;

  return (
    <RegistrationSetupWrapper>
      <RegistrationLayout
        containScroll
        header={
          <RegistrationSetupNavbar
            stepMeta={APPOINTMENT_STEP}
            totalSteps={REGISTRATION_SETUP_TOTAL_STEPS}
            onBack={() => registrationStepBack(history)}
          />
        }
      >
        <div className="scroll-body">
          <div
            className="content-card content-card-appointment"
            aria-busy={isWeekNavigating}
          >
            {isWeekNavigating ? (
              <div
                className="appointment-week-loading-overlay"
                aria-live="polite"
              >
                <div className="discovery-spinner-wrap">
                  <div className="discovery-spinner" aria-hidden />
                </div>
                <p className="section-sub">
                  {t("NUTRITION_TIMESLOT_LBL_LOADING")}
                </p>
              </div>
            ) : null}
            <div className="content-card-scroll">
              <div className="section-eyebrow appointment-step">
                {getAppointmentStepEyebrowLabel(t)}
              </div>
              <h2 className="section-title">
                {t("BY_RD_BTN_BROWSE_AVAILABLE_TIMES")}
              </h2>
              <p className="section-sub">
                {t("NUTRITION_TIMESLOT_DESC_SUBTITLE")}
              </p>

              {isLoading ? (
                <p className="section-sub">{t("NUTRITION_TIMESLOT_LBL_LOADING")}</p>
              ) : appointmentWeek ? (
                <div className="appointment-week-body">
                  <div className="appointment-week-header">
                    <div className="appointment-week-nav">
                      <button
                        type="button"
                        className="appointment-week-nav-btn"
                        onClick={() => handleWeekChange(-1)}
                        disabled={
                          weekOffset <= 0 || isLoading || isWeekNavigating
                        }
                        aria-label={t("NUTRITION_TIMESLOT_BTN_PREVIOUS_WEEK")}
                      >
                        <span className="appointment-week-nav-arrow" aria-hidden>
                          ‹
                        </span>
                        <span>{t("NUTRITION_TIMESLOT_BTN_PREVIOUS_WEEK")}</span>
                      </button>
                      <button
                        type="button"
                        className="appointment-week-nav-btn"
                        onClick={() => handleWeekChange(1)}
                        disabled={
                          weekOffset >= MAX_TIMESLOT_WEEK_OFFSET ||
                          isLoading ||
                          isWeekNavigating
                        }
                        aria-label={t("NUTRITION_TIMESLOT_BTN_NEXT_WEEK")}
                      >
                        <span>{t("NUTRITION_TIMESLOT_BTN_NEXT_WEEK")}</span>
                        <span className="appointment-week-nav-arrow" aria-hidden>
                          ›
                        </span>
                      </button>
                    </div>
                  </div>

                  <div
                    className="appointment-week-grid"
                    style={{
                      "--appointment-day-count": appointmentWeek.days.length,
                    }}
                  >
                    <div className="week-header">
                      {appointmentWeek.days.map((day) => (
                        <div key={day.dayKey}>
                          <div className="day-label">{day.label}</div>
                          <div className="day-date">{day.date}</div>
                        </div>
                      ))}
                    </div>

                    {!hasAvailableSlots ? (
                      <p className="section-sub">
                        {t("NUTRITION_TIMESLOT_DESC_NO_TIMES_WEEK")}{" "}
                        {t("NUTRITION_TIMESLOT_DESC_TRY_ANOTHER")}
                      </p>
                    ) : (
                      appointmentWeek.periods.map((period) => (
                        <div key={period.id} className="appointment-period">
                          <div className="slot-time-label">
                            {t(SLOT_SECTION_KEYS[period.id])}
                          </div>
                          {period.rows.map((row) => (
                            <div
                              key={`${period.id}-${row.time}`}
                              className="slots-grid"
                            >
                              {row.cells.map((cell, index) => {
                                const dayKey =
                                  cell.dayKey ??
                                  appointmentWeek.days[index]?.dayKey;
                                const isSelected =
                                  selectedSlot?.day === dayKey &&
                                  selectedSlot?.time === row.time;
                                if (cell.status === "booked") {
                                  return (
                                    <div
                                      key={`${row.time}-${index}`}
                                      className="slot slot-booked"
                                      aria-disabled
                                    >
                                      {row.time}
                                    </div>
                                  );
                                }
                                return (
                                  <button
                                    key={`${row.time}-${index}`}
                                    type="button"
                                    className={
                                      isSelected ? "slot slot-selected" : "slot"
                                    }
                                    onClick={() => handleSelectSlot(cell, row.time)}
                                  >
                                    {row.time}
                                  </button>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                      ))
                    )}
                  </div>

                  <div className="slot-legend">
                    <div className="slot-legend-item">
                      <span className="slot-legend-swatch slot-legend-available" />
                      {t("NUTRITION_TIMESLOT_LBL_LEGEND_AVAILABLE")}
                    </div>
                    <div className="slot-legend-item">
                      <span className="slot-legend-swatch slot-legend-booked" />
                      {t("NUTRITION_TIMESLOT_LBL_LEGEND_BOOKED")}
                    </div>
                    <div className="slot-legend-item">
                      <span className="slot-legend-swatch slot-legend-selected" />
                      {t("NUTRITION_TIMESLOT_LBL_LEGEND_SELECTED")}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </RegistrationLayout>

      <RegistrationNoPreferenceRdSlotModal
        isOpen={isRdModalOpen}
        onClose={() => setIsRdModalOpen(false)}
        slotLabel={modalSlotLabel}
        dietitians={modalDietitians}
        onViewProfile={handleViewProfile}
        onBookAppointment={handleBookAppointment}
      />

      <RegistrationLoadRetryModal
        isOpen={loadRetryModal.isOpen}
        onRetry={() => {
          const retry = loadRetryModal.onRetry;
          setLoadRetryModal({ isOpen: false, onRetry: null });
          retry?.();
        }}
        onCancel={() => {
          setLoadRetryModal({ isOpen: false, onRetry: null });
          history.replace("/register/account/dietitians");
        }}
        isRetrying={isLoading}
      />
    </RegistrationSetupWrapper>
  );
};

export default withRouter(RegistrationNoPreferenceAppointmentTime);
